import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { find, isNil, keys, length, propEq } from 'ramda';
import moment from 'moment';
import Avatar from '@components/@core/Avatar';
import EditDropdown from '@components/EditDropdown';
import { ManageReportMenu } from '~/services/config';

function Employees(props) {
    const navigate = useNavigate();
    const { data, role, jobRoles, onMenu, dataCy } = props;
    const users = useSelector((state) => state.app.users);
    return (
        <div className="list" data-cy={dataCy}>
            <div className="list-header desktop-screen">
                <div className="d-flex-3 dsl-m12 text-400" data-cy="employeeName">
                    Employee
                </div>
                <div data-cy="careerName" className="d-flex-3 ms-2 dsl-m12 text-400">
                    Career
                </div>
                <div data-cy="careerLevel" className="d-flex-2 ms-3 dsl-m12 text-400">
                    Level
                </div>
                <div data-cy="careerStarted" className="d-flex-2 dsl-m12 text-end text-400">
                    Started
                </div>
                <div data-cy="careerEstCompl" className="d-flex-2 dsl-m12 text-end text-400">
                    Est. Compl.
                </div>
                <div data-cy="careerCompleted" className="d-flex-2 me-2 dsl-m12 text-end text-400">
                    Completed
                </div>
                <div data-cy="threeDotSection" className="d-flex-1 dsl-m12" />
            </div>

            {data.map((item, index) => {
                const { stats } = item;
                const user = find(propEq('id', item.id), users);
                const profile = user?.profile || item.profile;
                const avatar = user?.avatar;
                let currentCareer = stats.open_careers[0];
                if (stats.open === 0 && stats.completed !== 0) {
                    currentCareer = stats.completed_careers[0];
                }
                const title = isNil(currentCareer) ? 'No Program Assigned' : currentCareer.title;
                const name = isNil(profile) ? '' : `${profile.first_name} ${profile.last_name}`;
                const careerRole = isNil(currentCareer) ? null : find(propEq('id', currentCareer.job_role_id), jobRoles);
                const jobTitle = isNil(careerRole) ? '' : careerRole.name;
                const level = isNil(currentCareer) ? 'N/A' : `(${currentCareer.level}/${isNil(currentCareer.data) || isNil(currentCareer.data.levels) ? 1 : length(keys(currentCareer.data.levels))})`;
                const started = isNil(currentCareer) ? 'N/A' : moment(currentCareer.started_at || currentCareer.created_at).format('MMM DD, YY');
                const end = isNil(currentCareer) || isNil(currentCareer.end_estimate) ? 'N/A' : moment(item.program.end_estimate).format('MMM DD, YY');
                let completed = 0;
                if (!isNil(currentCareer)) {
                    const currentStats = currentCareer.stats;
                    const courses = currentStats?.courses;
                    const quotas = currentStats?.quotas;
                    const totals = (isNil(quotas) ? 0 : quotas.total) + (isNil(courses) ? 0 : courses.total);
                    const completes = (isNil(quotas) ? 0 : quotas.complete) + (isNil(courses) ? 0 : courses.complete);
                    completed = totals === 0 ? 100 : ((completes * 100) / totals).toFixed(2);
                }
                let editOptions = [];
                if (isNil(currentCareer)) {
                    editOptions = ManageReportMenu[role].filter((option) => option === 'Assign Career');
                } else {
                    editOptions = ManageReportMenu[role].filter((option) => option !== 'Assign Career');
                }
                return (
                    <div className="d-flex ssm-column list-item cursor-pointer" data-cy={`employee-row${index}`} key={`crd${index}`}>
                        <div className="d-flex ssm-column ai-center d-flex-12 w-100" onClick={() => onMenu('view career', currentCareer, item)}>
                            <div className="d-flex d-flex-3 dsl-m12 ai-center jc-between w-100 py-2">
                                <div className="d-flex ai-center">
                                    <Avatar name={name} url={avatar} onToggle={() => navigate(`/library/record-employee-info/${item.id}`)} />
                                    <span className="dsl-b14 ms-3 me-2 text-400">{name}</span>
                                </div>
                                {editOptions.length !== 0 && (
                                    <EditDropdown className="mobile-screen" options={editOptions} onChange={(e) => onMenu(e, currentCareer, item)} dataCy={`employeeThreeDot${index}`} />
                                )}
                            </div>
                            <div className="d-flex-3 mx-3 dsl-m12 w-100 d-flex">
                                <label className="mobile-screen dsl-b14 w-30">Health Plan</label>
                                <p className="dsl-b14 text-400 wrap-title">{title}</p>
                            </div>
                            <div className="d-flex-2 dsl-m12 w-100 d-flex">
                                <label className="mobile-screen dsl-b14 w-30">Level</label>
                                <span data-cy="levelData" className="dsl-b14 text-400">{`${jobTitle} ${level}`}</span>
                            </div>
                            <div className="d-flex-2 dsl-m12 d-flex w-100 jc-end ssm-jc-start">
                                <label className="mobile-screen dsl-b14 w-30">Started</label>
                                <span className="dsl-b14 text-400">{started}</span>
                            </div>
                            <div className="d-flex-2 dsl-m12 d-flex w-100 jc-end ssm-jc-start">
                                <label className="mobile-screen dsl-b14 w-30">Est. Compl.</label>
                                <span className="dsl-b14 text-400">{end}</span>
                            </div>
                            <div className="d-flex-2 dsl-m12 d-flex w-100 jc-end ssm-jc-start">
                                <label className="mobile-screen dsl-b14 w-30">Completed</label>
                                <span className="dsl-b14 text-400">{completed}%</span>
                            </div>
                        </div>
                        <div className="d-flex-1 dsl-m12 text-end desktop-screen">
                            {editOptions.length !== 0 && <EditDropdown options={editOptions} onChange={(e) => onMenu(e, currentCareer, item)} dataCy={`employeeThreeDot${index}`} />}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

Employees.propTypes = {
    data: PropTypes.array,
    jobRoles: PropTypes.array,
    onMenu: PropTypes.func,
};

Employees.defaultProps = {
    data: [],
    jobRoles: [],
    onMenu: () => {},
};

export default Employees;
